import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Trophy, Sparkles, Crown } from 'lucide-react';
import confetti from 'canvas-confetti';
import { resetGame } from '../utils/api';
import { toast } from 'sonner';

export default function WinScreen() {
  const navigate = useNavigate();
  const confettiRef = useRef(null);
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    // Start continuous confetti
    const duration = 15 * 1000;
    const animationEnd = Date.now() + duration;
    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 9999 };

    function randomInRange(min, max) {
      return Math.random() * (max - min) + min;
    }

    const interval = setInterval(function() {
      const timeLeft = animationEnd - Date.now();

      if (timeLeft <= 0) {
        return clearInterval(interval);
      }

      const particleCount = 50 * (timeLeft / duration);

      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 }
      });
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 }
      });
    }, 250);

    return () => clearInterval(interval);
  }, []);

  const handlePlayAgain = async () => {
    if (resetting) return;
    
    setResetting(true);
    try {
      await resetGame(true);  // Pass true to keep high score
      toast.success('Game reset! Your high score remains on the leaderboard. Good luck!');
      navigate('/level-select');
    } catch (error) {
      toast.error('Failed to reset game. Please try again.');
      console.error('Reset error:', error);
    } finally {
      setResetting(false);
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-6 relative overflow-hidden"
      data-testid="win-screen"
      style={{
        backgroundImage: 'url(https://customer-assets.emergentagent.com/job_mega-church-game/artifacts/d1c9p3vy_FreeVector-Modern-City-Vector-Art.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      {/* Overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#4169E1]/40 via-[#9370DB]/40 to-[#FFD700]/30"></div>
      
      {/* Animated Background */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-1/4 w-32 h-32 bg-white/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/4 w-40 h-40 bg-white/20 rounded-full blur-3xl animate-pulse delay-500"></div>
      </div>

      <div className="relative z-10 text-center max-w-4xl">
        {/* Mega Church Logo */}
        <div className="flex justify-center mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_mega-church-game/artifacts/9qio0cok_Mega%20Church%20Logo%20T%20Shirt.png"
            alt="Mega Church Logo"
            className="w-32 h-32 object-contain drop-shadow-2xl"
          />
        </div>

        {/* Trophy Animation */}
        <div className="flex justify-center mb-8 animate-bounce">
          <div className="relative">
            <div className="absolute inset-0 bg-yellow-300 rounded-full blur-2xl opacity-50 animate-ping"></div>
            <div className="relative bg-gradient-to-br from-yellow-400 to-yellow-600 p-8 rounded-full shadow-2xl">
              <Trophy className="w-32 h-32 text-white" />
            </div>
          </div>
        </div>

        {/* Title */}
        <div className="mb-8">
          <div className="flex items-center justify-center gap-4 mb-4">
            <Sparkles className="w-12 h-12 text-yellow-300 animate-pulse" />
            <h1 className="text-7xl sm:text-8xl lg:text-9xl font-bold text-white drop-shadow-2xl" data-testid="win-title">
              YOU WIN!!!
            </h1>
            <Sparkles className="w-12 h-12 text-yellow-300 animate-pulse" />
          </div>
          <h2 className="text-5xl sm:text-6xl font-bold text-white mb-4">MEGA CHURCH</h2>
          <p className="text-2xl text-white/90 italic">"You've reached Mega Church status!"</p>
        </div>

        {/* Decorative Elements */}
        <div className="flex justify-center gap-8 mb-12">
          <Crown className="w-16 h-16 text-yellow-300 animate-bounce" />
          <Crown className="w-20 h-20 text-yellow-300 animate-bounce delay-100" />
          <Crown className="w-16 h-16 text-yellow-300 animate-bounce delay-200" />
        </div>

        {/* CTA Button */}
        <Button
          data-testid="mega-church-pro-btn"
          onClick={() => window.open('https://www.refugeplay.com', '_blank')}
          className="h-20 px-12 text-2xl font-bold bg-white text-[#4169E1] hover:bg-white/90 rounded-full shadow-2xl transform hover:scale-110 transition-all duration-300"
        >
          Play More Games
        </Button>

        <div className="mt-12">
          <Button
            data-testid="play-again-btn"
            onClick={handlePlayAgain}
            disabled={resetting}
            variant="outline"
            className="h-14 px-8 text-lg font-semibold bg-white/10 backdrop-blur-md text-white border-2 border-white/30 hover:bg-white/20 rounded-full disabled:opacity-50"
          >
            {resetting ? 'Resetting...' : 'Play Again'}
          </Button>
        </div>

      </div>
    </div>
  );
}
